import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filtered = articles.filter((a) => a.categorySlug === slug);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 黑料吃瓜网 | 实时追踪${category.name}爆料`;
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">
            分类不存在
          </h1>
          <Link
            to="/"
            className="text-primary-500 hover:underline text-sm"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://obyqdiy.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category.name,
        item: `http://obyqdiy.cn/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbList)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero */}
          <section className="relative min-h-[400px] md:min-h-[500px] flex items-end pb-10 md:pb-16 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={category.image}
                alt={`${category.name}分类头图`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
            </div>

            <div className="relative z-10 w-full px-4 md:px-8">
              <div className="max-w-7xl mx-auto">
                {/* Breadcrumb */}
                <nav aria-label="面包屑导航" className="mb-4">
                  <ol className="flex items-center gap-2 text-xs text-white/70">
                    <li>
                      <Link to="/" className="hover:text-white transition-colors">
                        首页
                      </Link>
                    </li>
                    <li>
                      <i className="ri-arrow-right-s-line" />
                    </li>
                    <li className="text-white">{category.name}</li>
                  </ol>
                </nav>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex -space-x-2">
                        {articles.slice(0, 3).map((a) => (
                          <img
                            key={a.id}
                            src={a.authorAvatar}
                            alt={a.author}
                            className="w-8 h-8 rounded-full border-2 border-white object-cover"
                          />
                        ))}
                      </div>
                      <span className="text-white/80 text-sm">
                        主编团队实时追踪
                      </span>
                    </div>

                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white leading-tight">
                      {category.name}
                    </h1>
                  </div>

                  <p className="hidden md:block text-white/80 text-sm md:text-base leading-relaxed text-right max-w-md ml-auto">
                    {category.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Article List */}
          <section className="py-12 md:py-16 px-4 md:px-8 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                最新<span className="text-primary-500">追踪</span>
              </h2>
              <span className="px-3 py-1 bg-foreground-800 text-white text-xs font-medium rounded-full">
                {category.name}
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-sm">
                  该分类暂无文章，敬请期待
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((article) => (
                  <article
                    key={article.id}
                    className="bg-background-100 rounded-xl overflow-hidden group cursor-pointer"
                  >
                    <Link to={`/article/${article.id}`} className="block">
                      <div className="w-full h-52 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] text-primary-600 font-semibold bg-primary-50 px-2 py-0.5 rounded">
                            {article.category}
                          </span>
                          <span className="text-xs text-foreground-400">
                            {article.publishDate}
                          </span>
                        </div>
                        <h3 className="text-sm font-bold text-foreground-900 mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src={article.authorAvatar}
                              alt={article.author}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                            <span className="text-xs text-foreground-400">
                              {article.author}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-foreground-700">
                            {article.heat.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </section>

          {/* Classic Review */}
          <section className="bg-background-100 py-12 md:py-16 px-4 md:px-8 max-w-7xl mx-auto mb-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                往期经典
              </h2>
              <p className="text-sm text-foreground-500 max-w-sm md:text-right">
                回顾那些引爆全网的经典爆料，每一个都是瓜友心中的意难忘
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {articles.slice(3, 6).map((article) => (
                <div
                  key={article.id}
                  className="bg-background-50 rounded-xl overflow-hidden group cursor-pointer"
                >
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-[10px] text-secondary-600 font-semibold bg-secondary-100 px-2 py-0.5 rounded">
                        经典回顾
                      </span>
                      <h3 className="text-sm font-bold text-foreground-900 mt-2 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1">
                        {article.views.toLocaleString()} 阅读
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}